import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { WATCHED_PERCENT, type TitleFacts, type VodItem, type WatchState } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { Translator } from '../i18n/i18n.js';

/**
 * Il pannello Info (03-SCHERMATE-TV §3.12): un modale con la sinossi estesa e la scheda tecnica.
 *
 * A sinistra kicker, titolo, meta, stato con la barra, sinossi; a destra la scheda in righe — regia,
 * cast, durata, anno, genere, qualità. In fondo i tasti: riprendi, «La mia lista», chiudi.
 *
 * ## Le righe senza dato non si disegnano
 *
 * È la regola che decide come questo pannello si comporta su un catalogo vero. Una riga «REGIA» con
 * niente accanto si legge come **un dato che non è arrivato** — e chi guarda conclude che l'app è
 * rotta. Una riga che non c'è dice invece che quel catalogo non lo ha, che è la cosa vera.
 *
 * Oggi la sorgente manda regia, cast, durata, anno, genere e voto. **Non manda** paese, sottotitoli,
 * lingue audio e classificazione d'età: quelle quattro righe del disegno non compaiono, e
 * compariranno da sole il giorno in cui qualcuno le importa — senza toccare questo file.
 *
 * ## Il fuoco resta dentro
 *
 * Un modale aperto prende i tasti e non li restituisce finché non si chiude (01-ARCHITETTURA, §1.1
 * della specifica): altrimenti le frecce muoverebbero **anche** la scheda sotto, che è il difetto per
 * cui si apre un pannello e si torna con un altro titolo in mano.
 */

export interface InfoPanelProps {
  readonly focusPort: FocusPort;
  readonly item: VodItem;
  readonly facts: TitleFacts | null;
  readonly state: WatchState;
  /** L'etichetta del tasto principale: la compone la scheda, che sa se è «Riprendi» o «Guarda S2 E3». */
  readonly primaryLabel: string;
  readonly onPrimary: () => void;
  readonly onToggleMyList?: () => void;
  readonly inMyList?: boolean;
  readonly onClose: () => void;
}

interface SheetRow {
  readonly labelKey: string;
  readonly value: string;
}

export function InfoPanel({
  focusPort,
  item,
  facts,
  state,
  primaryLabel,
  onPrimary,
  onToggleMyList,
  inMyList = false,
  onClose,
}: InfoPanelProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState(0);

  const handlers = useRef({ onPrimary, onToggleMyList, onClose });
  useEffect(() => {
    handlers.current = { onPrimary, onToggleMyList, onClose };
  }, [onPrimary, onToggleMyList, onClose]);

  const buttons = useMemo(() => {
    const entries = [{ id: 'primary', label: primaryLabel, run: () => handlers.current.onPrimary() }];
    if (onToggleMyList !== undefined) {
      entries.push({
        id: 'myList',
        label: translator.t(inMyList ? 'vod.action.inMyList' : 'vod.action.addMyList'),
        run: () => handlers.current.onToggleMyList?.(),
      });
    }
    entries.push({
      id: 'close',
      label: translator.t('vod.info.close'),
      run: () => handlers.current.onClose(),
    });
    return entries;
  }, [primaryLabel, onToggleMyList, inMyList, translator]);

  useEffect(() => {
    /*
      **I tasti sono suoi, tutti.**

      Nessun `return false`: qualunque tasto non riconosciuto si ferma qui invece di scendere alla
      scheda sotto. Un modale che lascia passare le frecce fa muovere due cose insieme, e si torna
      indietro con un titolo diverso da quello che si era aperto.
    */
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onClose();
        return true;
      }
      if (key.kind === 'select') {
        buttons[cursor]?.run();
        return true;
      }
      if (key.kind === 'direction') {
        if (key.direction === 'left') setCursor((at) => Math.max(0, at - 1));
        if (key.direction === 'right') setCursor((at) => Math.min(buttons.length - 1, at + 1));
      }
      return true;
    });
  }, [focusPort, buttons, cursor]);

  const rows = useMemo(() => sheetRows(item, facts, translator), [item, facts, translator]);

  return (
    <div
      data-info-panel="1"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(5,7,10,.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 72,
        boxSizing: 'border-box',
        zIndex: 10,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 1480,
          maxHeight: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          background: colors.panel,
          borderRadius: 24,
          padding: '48px 56px',
          display: 'flex',
          gap: 56,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span
            style={{
              font: `700 22px ${FONT_STACK}`,
              color: colors.accent,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
            }}
          >
            {[translator.t(item.kind === 'movie' ? 'vod.kind.movie' : 'vod.kind.series'), facts?.genre ?? item.genre]
              .filter((part) => part !== '')
              .join(' · ')}
          </span>
          <h2 style={{ font: `800 56px/1 ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
            {item.title}
          </h2>
          <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textMuted }}>
            {metaLine(item, facts, translator)}
          </span>
          <span
            data-info-status="1"
            style={{
              font: `700 22px ${FONT_STACK}`,
              color: state.watched ? colors.success : state.started ? colors.accent : colors.textDim,
            }}
          >
            {statusLabel(state, translator)}
          </span>
          {/* La barra solo quando c'è qualcosa da riprendere: piena o vuota non direbbe niente. */}
          {state.percent > 0 && state.percent < WATCHED_PERCENT && (
            <div style={{ height: 8, borderRadius: 4, background: colors.border, overflow: 'hidden', maxWidth: 520 }}>
              <div style={{ height: '100%', width: `${state.percent}%`, background: colors.accent }} />
            </div>
          )}
          {facts !== null && facts.plot !== '' && (
            <p style={{ font: `400 24px/1.45 ${FONT_STACK}`, color: colors.textMuted, margin: 0, maxWidth: 760 }}>
              {facts.plot}
            </p>
          )}

          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', paddingTop: 24, marginTop: 'auto' }}>
            {buttons.map((button, index) => {
              const focused = index === cursor;
              return (
                <button
                  key={button.id}
                  type="button"
                  data-f="1"
                  data-info-action={button.id}
                  data-focused={focused ? '1' : '0'}
                  onClick={button.run}
                  style={{
                    height: 72,
                    padding: '0 32px',
                    border: 0,
                    borderRadius: 14,
                    cursor: 'pointer',
                    background: button.id === 'primary' ? colors.text : colors.surfaceButton,
                    color: button.id === 'primary' ? colors.background : colors.text,
                    font: `${button.id === 'primary' ? 700 : 600} 26px ${FONT_STACK}`,
                    outline: focused ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 4,
                    transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                    transition: 'var(--tr, transform .16s ease)',
                  }}
                >
                  {button.label}
                </button>
              );
            })}
          </div>
        </div>

        {rows.length > 0 && (
          <div
            data-info-sheet="1"
            style={{
              flex: 'none',
              width: 520,
              display: 'flex',
              flexDirection: 'column',
              gap: 22,
              borderLeft: `1px solid ${colors.borderFaint}`,
              paddingLeft: 40,
            }}
          >
            {rows.map((row) => (
              <div key={row.labelKey} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span
                  style={{
                    font: `600 18px ${FONT_STACK}`,
                    color: colors.textFaint,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {translator.t(row.labelKey)}
                </span>
                <span style={{ font: `500 24px/1.3 ${FONT_STACK}`, color: colors.text }}>{row.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Le righe della scheda tecnica, **solo quelle che hanno un valore**.
 *
 * L'ordine è quello del disegno. Paese, sottotitoli, audio e classificazione non ci sono perché la
 * sorgente non li manda: compariranno da sole il giorno in cui qualcuno li importa, senza toccare
 * questo file — è il motivo per cui l'elenco si costruisce filtrando invece di essere scritto fisso.
 */
function sheetRows(item: VodItem, facts: TitleFacts | null, translator: Translator): SheetRow[] {
  const candidates: Array<{ labelKey: string; value: string | null }> = [
    { labelKey: 'vod.info.director', value: facts?.director ?? null },
    { labelKey: 'vod.info.cast', value: facts?.cast ?? null },
    {
      labelKey: 'vod.info.duration',
      value: facts?.durationMin != null && facts.durationMin > 0 ? durationLabel(facts.durationMin, translator) : null,
    },
    {
      labelKey: 'vod.info.year',
      value: (facts?.year ?? item.year) > 0 ? String(facts?.year ?? item.year) : null,
    },
    { labelKey: 'vod.info.genre', value: facts?.genre !== undefined && facts.genre !== '' ? facts.genre : item.genre || null },
    {
      labelKey: 'vod.info.rating',
      value:
        facts?.rating != null
          ? `${facts.rating.toFixed(1)}${facts.voteCount != null ? ` · ${translator.t('vod.votes', { count: facts.voteCount })}` : ''}`
          : null,
    },
  ];
  return candidates
    .filter((row): row is { labelKey: string; value: string } => row.value !== null && row.value !== '')
    .map((row) => ({ labelKey: row.labelKey, value: row.value }));
}

/** «2 h 2 min», come il disegno: le ore si scrivono solo quando ci sono. */
export function durationLabel(totalMinutes: number, translator: Translator): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  if (hours <= 0) return translator.t('vod.minutes', { count: minutes });
  return translator.t('vod.hoursMinutes', { hours, minutes });
}

function metaLine(item: VodItem, facts: TitleFacts | null, translator: Translator): string {
  const parts: string[] = [];
  const year = facts?.year ?? item.year;
  if (year > 0) parts.push(String(year));
  if (facts?.durationMin != null && facts.durationMin > 0) {
    parts.push(durationLabel(facts.durationMin, translator));
  }
  return parts.join(' · ');
}

function statusLabel(state: WatchState, translator: Translator): string {
  if (state.watched) return translator.t('vod.status.watched');
  if (state.totalEpisodes > 0 && state.doneEpisodes > 0) {
    return translator.t('vod.status.episodes', { done: state.doneEpisodes, total: state.totalEpisodes });
  }
  if (state.percent > 0) return translator.t('vod.status.percent', { percent: state.percent });
  return translator.t('vod.status.new');
}
